/* iter55g4 — 5-tier Character Bible sub-panel.
 * Renders inside CharacterBiblePanel, right beneath the Voice Sheet.
 * Fetches the tier field spec once, then lets writers edit each field
 * inline (blur → PUT /api/characters/{cid}) or ask Claude to fill an
 * entire tier from a short prompt.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Wand2, Loader2, Layers, ArrowRightLeft, Share2, CheckCircle2, Zap, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { codexApi } from '@/lib/api';
import { seriesSyncApi } from '@/lib/api';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Module-level cache — the schema never changes across a session so we
// avoid a re-fetch on every character switch.
let _cachedSchema = null;
async function fetchSchema() {
  if (_cachedSchema) return _cachedSchema;
  try {
    const r = await axios.get(`${API}/characters/bible/schema`, { withCredentials: true });
    _cachedSchema = r.data;
    return _cachedSchema;
  } catch {
    return null;
  }
}

export default function FiveTierBible({ character, onCharacterUpdate }) {
  const [schema,   setSchema]   = useState(null);
  const [activeT,  setActiveT]  = useState('identity');
  const [fillOpen, setFillOpen] = useState(false);
  const [fillPrompt, setFillPrompt] = useState('');
  const [fillLoading, setFillLoading] = useState(false);
  const [fillGapsLoading, setFillGapsLoading] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);

  // iter55g7 — Codex publish state.
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishTags, setPublishTags] = useState('');
  const [publishSummary, setPublishSummary] = useState('');
  const [publishLoading, setPublishLoading] = useState(false);
  const [codexEntryId, setCodexEntryId] = useState(null);

  // Series Sync state — reads the canonical `series_carryover` field returned
  // by the characters API.  `series_sync_enabled` is a DB-only alias kept for
  // bulk-push queries; we don't rely on it being present in the API response.
  const [seriesSyncEnabled, setSeriesSyncEnabled] = useState(character.series_carryover ?? false);
  const [syncToProjectId,   setSyncToProjectId]   = useState('');
  const [syncPushing,       setSyncPushing]        = useState(false);
  const [syncOpen,          setSyncOpen]           = useState(false);
  const [projects,          setProjects]           = useState([]);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

  useEffect(() => { fetchSchema().then(setSchema); }, []);

  // Re-sync the toggle whenever the active character changes or is updated from
  // a parent store refresh (e.g. CharacterBiblePanel's own bulk-sync path).
  useEffect(() => {
    setSeriesSyncEnabled(character.series_carryover ?? false);
  }, [character.id, character.series_carryover]);

  // On character switch, check if it's already published to the Codex.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const rows = await codexApi.mine();
        if (cancel) return;
        const match = (rows || []).find((r) => r.character_id === character.id);
        setCodexEntryId(match ? match.id : null);
      } catch { /* offline / anon — ignore */ }
    })();
    return () => { cancel = true; };
  }, [character.id]);

  const bible = useMemo(() => character.bible || {}, [character.bible]);
  // Is every tier empty? Show the "Migrate from legacy" affordance if so
  // AND the character has legacy fields to migrate from.
  const hasAnyTier = useMemo(
    () => (schema?.tiers || []).some((t) => Object.keys(bible[t.key] || {}).length > 0),
    [schema, bible],
  );
  const hasLegacy = !!(character.description || character.voice || character.arc_summary);

  const activeSpec = useMemo(
    () => (schema?.tiers || []).find((t) => t.key === activeT) || null,
    [schema, activeT],
  );

  const persistField = async (tierKey, fieldKey, value) => {
    const nextTier = { ...(bible[tierKey] || {}) };
    // style_anchor is a long-form prose field (300-500 words ≈ 3200 chars);
    // all other bible fields are short factual values (≤220 chars).
    const maxLen = fieldKey === 'style_anchor' ? 3200 : 220;
    if (value == null || String(value).trim() === '') {
      delete nextTier[fieldKey];
    } else {
      nextTier[fieldKey] = String(value).slice(0, maxLen);
    }
    const nextBible = { ...bible, [tierKey]: nextTier };
    try {
      const r = await axios.put(
        `${API}/characters/${character.id}`,
        { bible: nextBible },
        { withCredentials: true },
      );
      if (onCharacterUpdate) onCharacterUpdate(r.data);
    } catch (e) {
      toast.error('Could not save that field.', {
        description: e?.response?.data?.detail || 'Try again in a moment.',
      });
    }
  };

  // Count total missing fields across all tiers (for the "Fill N gaps" button badge).
  const missingCount = useMemo(() => {
    if (!schema) return 0;
    return schema.tiers.reduce((sum, tier) => {
      const existing = bible[tier.key] || {};
      return sum + tier.fields.filter(f => !existing[f.key]).length;
    }, 0);
  }, [schema, bible]);

  const runFillGaps = async () => {
    if (fillGapsLoading) return;
    setFillGapsLoading(true);
    const toastId = toast.loading(`Filling ${missingCount} gap${missingCount === 1 ? '' : 's'} across all tiers…`);
    try {
      const r = await axios.post(
        `${API}/characters/${character.id}/bible/fill-gaps`,
        {},
        { withCredentials: true },
      );
      toast.dismiss(toastId);
      if (onCharacterUpdate) onCharacterUpdate(r.data.character);
      if (r.data.total_filled === 0) {
        toast.info('No gaps found — the bible is already fully populated.');
      } else {
        const tierNames = Object.keys(r.data.tiers_filled)
          .map(k => schema?.tiers.find(t => t.key === k)?.label || k)
          .join(', ');
        toast.success(`Filled ${r.data.total_filled} field${r.data.total_filled === 1 ? '' : 's'}: ${tierNames}`);
      }
    } catch (e) {
      toast.dismiss(toastId);
      toast.error('Gap fill failed.', { description: e?.response?.data?.detail || 'Try again in a moment.' });
    } finally {
      setFillGapsLoading(false);
    }
  };


  const runFill = async () => {
    if (!fillPrompt.trim() || !activeSpec) return;
    setFillLoading(true);
    try {
      const r = await axios.post(
        `${API}/characters/${character.id}/bible/fill`,
        { tier: activeSpec.key, prompt: fillPrompt.trim() },
        { withCredentials: true },
      );
      if (onCharacterUpdate) onCharacterUpdate(r.data.character);
      const filledCount = Object.keys(r.data.filled || {}).length;
      if (r.data.llm_error) {
        toast.error('Claude was unavailable, nothing filled.', { description: r.data.llm_error });
      } else if (filledCount === 0) {
        toast.info(`No new fields to add — Claude preserved your existing "${activeSpec.label}".`);
      } else {
        toast.success(`Filled ${filledCount} "${activeSpec.label}" field${filledCount === 1 ? '' : 's'}.`);
      }
      setFillOpen(false); setFillPrompt('');
    } catch (e) {
      toast.error('Fill failed.', { description: e?.response?.data?.detail || 'Try a shorter prompt.' });
    } finally {
      setFillLoading(false);
    }
  };

  const runMigrate = async () => {
    setMigrateLoading(true);
    try {
      const r = await axios.post(
        `${API}/characters/${character.id}/bible/migrate`,
        {},
        { withCredentials: true },
      );
      if (r.data.skipped) {
        toast.info('Bible already populated — nothing to migrate.');
      } else {
        if (onCharacterUpdate) onCharacterUpdate(r.data.character);
        toast.success('Legacy notes split into the 5-tier bible.', {
          description: 'Review each tab and edit any field that needs a polish.',
        });
      }
    } catch (e) {
      toast.error('Migration failed.', { description: e?.response?.data?.detail || '' });
    } finally {
      setMigrateLoading(false);
    }
  };

  // iter55g7 — Publish this character to the Community Codex.
  const populatedTiers = useMemo(
    () => Object.values(bible).filter((t) => t && Object.keys(t).length > 0).length,
    [bible],
  );
  const canPublish = populatedTiers >= 3;

  const runPublish = async () => {
    if (!canPublish) {
      toast.error('Populate at least 3 tiers before publishing.');
      return;
    }
    setPublishLoading(true);
    try {
      const tags = publishTags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 8);
      const r = await codexApi.publish({
        character_id: character.id,
        tags,
        summary: publishSummary.trim(),
      });
      setCodexEntryId(r.entry.id);
      toast.success(codexEntryId ? 'Codex entry updated.' : 'Character published to the Codex!', {
        description: 'Other writers can now find and import your character.',
      });
      setPublishOpen(false); setPublishTags(''); setPublishSummary('');
    } catch (e) {
      toast.error('Publish failed.', { description: e?.response?.data?.detail || '' });
    } finally {
      setPublishLoading(false);
    }
  };
  const runUnpublish = async () => {
    if (!codexEntryId) return;
    try {
      await codexApi.unpublish(character.id);
      setCodexEntryId(null);
      toast.success('Removed from the Codex.');
    } catch (e) {
      toast.error('Could not unpublish.', { description: e?.response?.data?.detail || '' });
    }
  };

  // ── Series Sync ───────────────────────────────────────────────────────────
  const toggleSeriesSync = async (next) => {
    setSeriesSyncEnabled(next);
    try {
      await seriesSyncApi.toggle(character.id, next);
      // Propagate to the parent store so the "Book 2+" badge in the character
      // list updates immediately without a page reload.
      onCharacterUpdate?.({ ...character, series_carryover: next, series_sync_enabled: next });
      toast.success(
        next
          ? `${character.name} will carry over to Book 2+.`
          : `${character.name} removed from Book 2+ carryover.`,
      );
    } catch (e) {
      setSeriesSyncEnabled(!next);
      toast.error('Could not update series carryover — try again.');
    }
  };

  const openSyncDialog = async () => {
    setSyncOpen(true);
    if (projects.length === 0) {
      try {
        const r = await axios.get(`${API_BASE}/projects`, { withCredentials: true });
        const list = Array.isArray(r.data) ? r.data : (r.data.projects || []);
        setProjects(list.filter(p => p.id !== character.project_id));
      } catch { /* ignore */ }
    }
  };

  const pushSeriesSync = async () => {
    if (!syncToProjectId) { toast.error('Select a target project.'); return; }
    setSyncPushing(true);
    try {
      const res = await seriesSyncApi.push(character.id, syncToProjectId);
      toast.success(
        res.action === 'created'
          ? `${res.name} added to Book 2+ project.`
          : `${res.name} updated in Book 2+ project.`
      );
      setSyncOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync push failed.');
    } finally { setSyncPushing(false); }
  };

  if (!schema) {
    return (
      <div className="rounded-md border border-panel-border bg-card p-3" data-testid="five-tier-bible-loading">
        <span className="text-[13px] text-muted-foreground inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading 5-tier Character Bible…
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-panel-border bg-card p-3" data-testid="five-tier-bible">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers className="h-3.5 w-3.5 text-primary" />
          <span className="text-[13px] uppercase tracking-wider text-muted-foreground">
            5-Tier Character Bible
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!hasAnyTier && hasLegacy && (
            <Button
              variant="outline" size="sm"
              className="h-6 gap-1 px-1.5 text-[13px]"
              onClick={runMigrate}
              disabled={migrateLoading}
              data-testid="bible-migrate-btn"
              title="Split your existing description / voice / arc into the 5 tiers"
            >
              {migrateLoading
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <ArrowRightLeft className="h-3 w-3" />}
              Migrate legacy
            </Button>
          )}
          {/* "Fill N gaps" — auto-fills every empty field across all tiers. */}
          {missingCount > 0 && (
            <Button
              variant="outline" size="sm"
              className="h-6 gap-1 px-1.5 text-[13px] border-amber-300/70 text-amber-700 hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-400 dark:hover:bg-amber-500/10"
              onClick={runFillGaps}
              disabled={fillGapsLoading}
              data-testid="bible-fill-gaps-btn"
              title={`Auto-fill ${missingCount} empty fields across all tiers from this character's profile`}
            >
              {fillGapsLoading
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Zap className="h-3 w-3" />}
              Fill {missingCount} gap{missingCount === 1 ? '' : 's'}
            </Button>
          )}
          <Button
            variant="ghost" size="sm"
            className="h-6 gap-1 px-1.5 text-[13px]"
            onClick={() => { setFillOpen(true); setFillPrompt(''); }}
            data-testid="bible-fill-btn"
            title={`Ask Claude to fill the "${activeSpec?.label || 'current'}" tier`}
          >
            <Sparkles className="h-3 w-3" /> AI fill this tier
          </Button>
          {/* iter55g7 — Publish to Community Codex. Shows only when the
               bible has 3+ populated tiers (matches backend gate). */}
          {codexEntryId ? (
            <Button
              variant="outline" size="sm"
              className="h-6 gap-1 border-emerald-300 bg-emerald-50 px-1.5 text-[13px] text-emerald-800 hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-100"
              onClick={runUnpublish}
              data-testid="bible-codex-unpublish-btn"
              title="Currently on the public Codex — click to unpublish"
            >
              <CheckCircle2 className="h-3 w-3" /> On the Codex
            </Button>
          ) : canPublish ? (
            <Button
              variant="ghost" size="sm"
              className="h-6 gap-1 px-1.5 text-[13px]"
              onClick={() => { setPublishOpen(true); setPublishTags(''); setPublishSummary(''); }}
              data-testid="bible-codex-publish-btn"
              title="Share this character with the community"
            >
              <Share2 className="h-3 w-3" /> Publish to Codex
            </Button>
          ) : null}
        </div>
      </div>

      {/* ── Series Sync strip ─────────────────────────────────────────── */}
      <div
        className="mb-2 flex items-center justify-between gap-2 rounded-sm border border-dashed border-violet-300/60 bg-violet-50/40 px-2.5 py-1.5 dark:border-violet-500/30 dark:bg-violet-500/5"
        data-testid="series-sync-strip"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <ArrowRightLeft className="h-3 w-3 shrink-0 text-violet-600 dark:text-violet-400" />
          <span className="text-[11px] font-semibold text-violet-700 dark:text-violet-300 truncate">
            Series Carry-over
          </span>
          <span className="text-[10px] text-violet-500/80 dark:text-violet-400/70 truncate hidden sm:inline">
            physical · role · arc · notes
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Toggle */}
          <button
            type="button"
            role="switch"
            aria-checked={seriesSyncEnabled}
            onClick={() => toggleSeriesSync(!seriesSyncEnabled)}
            className={cn(
              'relative inline-flex h-4 w-7 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none',
              seriesSyncEnabled
                ? 'bg-violet-500'
                : 'bg-neutral-200 dark:bg-neutral-700',
            )}
            data-testid="series-sync-toggle"
            title={seriesSyncEnabled ? 'Disable series carry-over' : 'Enable series carry-over'}
          >
            <span
              className={cn(
                'pointer-events-none block h-3 w-3 rounded-full bg-white shadow-md ring-0 transition-transform',
                seriesSyncEnabled ? 'translate-x-3' : 'translate-x-0',
              )}
            />
          </button>
          {/* Push button — only when enabled */}
          {seriesSyncEnabled && (
            <Button
              variant="outline" size="sm"
              className="h-5 gap-1 border-violet-300 px-1.5 text-[10px] text-violet-700 hover:bg-violet-50 dark:border-violet-500/40 dark:text-violet-400"
              onClick={openSyncDialog}
              data-testid="series-sync-push-btn"
              title="Push physical traits, role, arc status, and custom notes to a Book 2+ project"
            >
              <ArrowRightLeft className="h-2.5 w-2.5" /> Sync to Book 2+
            </Button>
          )}
        </div>
      </div>

      {/* Series Sync push dialog */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="max-w-sm" data-testid="series-sync-dialog">
          <DialogHeader>
            <DialogTitle>Sync Character to Book 2+</DialogTitle>
            <DialogDescription>
              Physical traits, story role, arc status, and custom notes will be copied to the selected project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="block">
              <span className="text-[12px] font-medium text-foreground mb-1 block">Target project</span>
              <select
                value={syncToProjectId}
                onChange={(e) => setSyncToProjectId(e.target.value)}
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-[13px] outline-none focus:border-primary"
                data-testid="series-sync-project-select"
              >
                <option value="">Select a project…</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.title || '(Untitled)'}</option>
                ))}
              </select>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setSyncOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              onClick={pushSeriesSync}
              disabled={syncPushing || !syncToProjectId}
              className="gap-1"
              data-testid="series-sync-confirm-btn"
            >
              {syncPushing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />}
              {syncPushing ? 'Syncing…' : 'Push to Book 2+'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tier tabs */}
      <div className="mb-2 flex flex-wrap gap-1" role="tablist" data-testid="bible-tabs">
        {schema.tiers.map((t) => {
          const count = Object.keys(bible[t.key] || {}).length;
          const total = t.fields.length;
          const isVoiceRegional = t.key === 'voice_regional';
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeT === t.key}
              onClick={() => setActiveT(t.key)}
              data-testid={`bible-tab-${t.key}`}
              className={cn(
                'group flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[13px] transition-colors',
                activeT === t.key
                  ? isVoiceRegional
                    ? 'border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                    : 'border-primary bg-primary/10 text-primary'
                  : isVoiceRegional
                    ? 'border-emerald-400/50 bg-emerald-50/60 text-emerald-700 hover:bg-emerald-100/70 dark:border-emerald-500/30 dark:bg-emerald-500/8 dark:text-emerald-400 dark:hover:bg-emerald-500/15'
                    : 'border-panel-border bg-background text-muted-foreground hover:bg-secondary',
              )}
              title={isVoiceRegional ? 'Regional Voice & Tone — critical for multilingual manuscripts' : undefined}
            >
              {isVoiceRegional && (
                <Globe className={cn('h-3 w-3 shrink-0', activeT === t.key ? 'text-emerald-600 dark:text-emerald-400' : 'text-emerald-500 dark:text-emerald-500')} />
              )}
              <span className="font-bold">{t.label}</span>
              {isVoiceRegional && count === 0 && (
                <span className="ml-0.5 inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-100 px-1 text-[8.5px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                  new
                </span>
              )}
              <span className={cn(
                'inline-flex min-w-[22px] justify-center rounded-full px-1 text-[9px] font-semibold',
                count > 0
                  ? isVoiceRegional ? 'bg-emerald-200/80 text-emerald-800 dark:bg-emerald-500/25 dark:text-emerald-300' : 'bg-primary/20 text-primary'
                  : 'bg-secondary text-muted-foreground',
              )}>
                {count}/{total}
              </span>
            </button>
          );
        })}
      </div>

      {/* voice_regional tier spotlight hint (shown when active) */}
      {activeT === 'voice_regional' && (
        <div className="mb-2 flex items-start gap-2 rounded-sm border border-emerald-400/40 bg-emerald-50/60 px-2.5 py-2 dark:bg-emerald-500/10 dark:border-emerald-500/25" data-testid="voice-regional-spotlight">
          <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <p className="text-[13px] text-emerald-800 dark:text-emerald-300 leading-snug">
            <span className="font-semibold">Regional Voice & Tone</span> — Pin this character&apos;s dialect, register, and emotional grammar here. The Voice Drift checker and all AI generation will imitate the <em>Style Anchor</em> you paste in, making the prose sound authentically local rather than generic.
          </p>
        </div>
      )}

      {activeSpec && (
        <>
          <p className="mb-2 text-[13px] italic text-foreground/70" data-testid={`bible-tier-hint-${activeSpec.key}`}>
            {activeSpec.hint}
          </p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2" data-testid={`bible-tier-${activeSpec.key}-fields`}>
            {activeSpec.fields.map((f) => {
              const current = (bible[activeSpec.key] || {})[f.key] || '';
              return (
                <label
                  key={f.key}
                  className={cn(
                    'flex flex-col gap-0.5',
                    f.key === 'style_anchor' && 'md:col-span-2',
                  )}
                >
                  <span className="text-[13px] font-bold uppercase tracking-wider text-foreground">
                    {f.label}
                  </span>
                  {/* Long-form fields (style anchor = 300-500 words) get a Textarea;
                      all other short factual fields keep a single-line Input. */}
                  {f.key === 'style_anchor' ? (
                    <Textarea
                      key={`${character.id}-${activeSpec.key}-${f.key}`}
                      defaultValue={current}
                      placeholder={f.placeholder}
                      maxLength={3200}
                      rows={8}
                      onBlur={(e) => {
                        if (e.target.value !== current) {
                          persistField(activeSpec.key, f.key, e.target.value);
                        }
                      }}
                      data-testid={`bible-field-${activeSpec.key}-${f.key}`}
                      className="text-[13.5px] resize-y min-h-[140px]"
                    />
                  ) : (
                  <Input
                    key={`${character.id}-${activeSpec.key}-${f.key}`}
                    defaultValue={current}
                    placeholder={f.placeholder}
                    maxLength={220}
                    onBlur={(e) => {
                      if (e.target.value !== current) {
                        persistField(activeSpec.key, f.key, e.target.value);
                      }
                    }}
                    data-testid={`bible-field-${activeSpec.key}-${f.key}`}
                    className="h-7 text-[13.5px]"
                  />
                  )}
                </label>
              );
            })}
          </div>
        </>
      )}

      {/* AI fill dialog */}
      <Dialog open={fillOpen} onOpenChange={setFillOpen}>
        <DialogContent className="max-w-md" data-testid="bible-fill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-primary" />
              AI fill · {activeSpec?.label}
            </DialogTitle>
            <DialogDescription className="text-[13.5px]">
              Give Claude a short seed — a phrase, a mood, a comparison to another
              character — and it will populate the empty fields in this tier while
              preserving anything you&apos;ve already written.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={fillPrompt}
            onChange={(e) => setFillPrompt(e.target.value)}
            placeholder={`e.g. "grew up in a smuggler's port; hates authority; secretly fluent in three languages"`}
            rows={3}
            maxLength={2000}
            data-testid="bible-fill-prompt"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFillOpen(false)} disabled={fillLoading}>
              Cancel
            </Button>
            <Button
              onClick={runFill}
              disabled={fillLoading || !fillPrompt.trim()}
              data-testid="bible-fill-confirm"
            >
              {fillLoading
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5 mr-1" />}
              Fill tier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter55g7 — Publish to Community Codex dialog. */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="max-w-md" data-testid="bible-codex-publish-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-primary" />
              Publish {character.name || 'this character'} to the Codex
            </DialogTitle>
            <DialogDescription className="text-[13.5px]">
              Share this 5-tier bible on the public /codex page so other writers
              can browse it and — with one click — import a copy into their own
              projects. You can update or unpublish any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[13px] uppercase tracking-wider text-foreground/70">Tags (comma-separated, max 8)</span>
              <Input
                value={publishTags}
                onChange={(e) => setPublishTags(e.target.value)}
                placeholder="fantasy, cartographer, reluctant-hero"
                data-testid="bible-codex-publish-tags"
                maxLength={200}
                className="h-7 text-[13.5px]"
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[13px] uppercase tracking-wider text-foreground/70">Short summary (max 280 chars)</span>
              <Textarea
                value={publishSummary}
                onChange={(e) => setPublishSummary(e.target.value)}
                placeholder="A precise cartographer forced onto the road when the empire outlaws maps of the coast."
                rows={3}
                maxLength={280}
                data-testid="bible-codex-publish-summary"
                className="text-[13.5px]"
              />
            </label>
            <div className="rounded-sm bg-secondary/50 px-2 py-1.5 text-[13px] text-foreground/70">
              {populatedTiers} / {schema.tiers.length} tiers populated · you can unpublish at any time.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPublishOpen(false)} disabled={publishLoading}>
              Cancel
            </Button>
            <Button
              onClick={runPublish}
              disabled={publishLoading || !canPublish}
              data-testid="bible-codex-publish-confirm"
            >
              {publishLoading
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <Share2 className="h-3.5 w-3.5 mr-1" />}
              {codexEntryId ? 'Update Codex entry' : 'Publish to Codex'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
